import { PI_VERSION } from '../constants-types/constants';
import { CacheMapHashFunction, SerializedCacheEntry } from './Cache.interface';
import { CacheEntry } from './CacheEntry';

const cachePrefix = 'pi-cache_';
const cacheVersionNumberKey = cachePrefix + 'VERSION';

/**
 * Cache class.
 * This is genericized on the type of <data being stored>, T, as well as the
 * <arguments passed to the hash function>, H.
 *
 * For example if you had a cache that stored phone numbers of users and required
 * their <userId: string> and their <userData: UserData> to compute the hash the
 * declaration might look like this:
 *
 * private cache = new CacheMap<number, [string, UserData]>(
 *     (hashArgs: [string, UserData]) => hashArgs[0] + hashArgs[1].firstName
 * );
 */
export class Cache<T, H> {
    private map: { [key: string]: CacheEntry<T> };

    constructor(
        private name: string,
        private hashFunction: CacheMapHashFunction<H>
    ) {
        // For debugging cache
        if (window.location.search.indexOf('clearCache') !== -1) {
            Cache.clearAllCacheEntries();
            window.location.search = '';
        }

        this.map = this.loadFromLocalStorage();

        window.addEventListener('beforeunload', () => this.destroy());
    }

    static clearAllCacheEntries(): void {
        const allKeys = this.getAllLocalStorageKeys();
        allKeys
            .filter((key) => key.indexOf(cachePrefix) !== -1)
            .forEach((key) => localStorage.removeItem(key));
    }

    private getItemKeyPrefix(): string {
        return cachePrefix + this.name + ':';
    }

    private getItemKey(key: string): string {
        return this.getItemKeyPrefix() + key;
    }

    private static getAllLocalStorageKeys(): string[] {
        const allKeys = [];
        for (let i = 0; i < localStorage.length; i++) {
            allKeys.push(localStorage.key(i));
        }
        return allKeys;
    }

    private getLocalStorageKeys(): string[] {
        const allKeys = Cache.getAllLocalStorageKeys();
        return allKeys.filter(
            (key) => key.indexOf(this.getItemKeyPrefix()) !== -1
        );
    }

    private getInternalKeyFromLocalStorageKey(localStorageKey: string): string {
        return localStorageKey.split(this.getItemKeyPrefix())[1];
    }

    private loadFromLocalStorage(): any {
        const map: {
            [key: string]: CacheEntry<{}>;
        } = {};

        // If the version number has updated since data was stored we don't load
        // any of the old data
        const previousVersion = localStorage.getItem(cacheVersionNumberKey);
        if (previousVersion !== PI_VERSION) {
            return map;
        }

        const storedKeys = this.getLocalStorageKeys();
        if (storedKeys.length) {
            storedKeys.forEach((key) => {
                const stored = localStorage.getItem(key);
                let serialized: SerializedCacheEntry<T>;
                try {
                    serialized = JSON.parse(stored);
                } catch (e) {
                    throw new Error(
                        `Error parsing localStorage entry: ${stored}`
                    );
                }
                localStorage.removeItem(key);
                if (serialized) {
                    const deserialized = new CacheEntry(null);
                    deserialized.deserialize(serialized);

                    const internalKey =
                        this.getInternalKeyFromLocalStorageKey(key);
                    map[internalKey] = deserialized;
                }
            });
        }
        return map;
    }

    destroy() {
        // First store the current app version number
        localStorage.setItem(cacheVersionNumberKey, PI_VERSION);

        // Sort by newest so we can write the newest entries and if we hit
        // the localStorage quota we can leave out the older entries
        const sorted = Object.keys(this.map)
            .map((key) => {
                const entry = this.map[key];
                return {
                    key,
                    entry
                };
            })
            .sort((a, b) => b.entry.time - a.entry.time);

        return Promise.all(
            sorted.map((wrappedEntry) =>
                wrappedEntry.entry.serialize().then((serializedData) => {
                    try {
                        return localStorage.setItem(
                            this.getItemKey(wrappedEntry.key),
                            JSON.stringify(serializedData)
                        );
                    } catch (e) {
                        return Cache.clearAllCacheEntries();
                    }
                })
            )
        );
    }

    /**
     * Get the element from the cache.
     * Pass in -1 for the expirationTime to skip expiration checks.
     *
     * @param hashArguments
     * @param expirationTime
     */
    get(hashArguments: H, expirationTime: number): Promise<T> | null {
        const key = this.hashFunction(hashArguments);
        const hit = this.map[key];

        if (
            hit &&
            // Ignore cached null values
            hit.data &&
            // Ignore expiration time if it's less than 0
            (expirationTime < 0 || !hit.hasExpired(expirationTime))
        ) {
            return hit.data;
        }
        return null;
    }

    set(hashArguments: H, value: Promise<T>): void {
        const key = this.hashFunction(hashArguments);
        this.map[key] = new CacheEntry(value);
    }

    clear(hashArguments: H): void {
        const key = this.hashFunction(hashArguments);
        delete this.map[key];
    }

    clearAll(): void {
        this.map = {};
    }
}
